import { useState } from 'react';
import { createPortal } from 'react-dom';
import { pickLocale, type Component } from '../schema';
import { useEffectiveSelections } from '../state/useEffectiveSelections';
import { collectCombinedImageLayers } from './combinedImage/layers';
import { useExternalMount } from './useExternalMount';

interface CombinedImageProps {
    component: Component;
    locale: string | undefined;
    /**
     * Optional CSS selector for an external DOM element on the host page
     * that should receive the layer stack via React Portal. Used by
     * storefront plugins (Magento default `div.gallery-placeholder`,
     * `css_path_combined_image` config) so the configurator-driven image
     * replaces the merchant's static product photo. Set from
     * `combinedImageMount` on the WebshopConfig — via data attribute or the
     * preview context — and no longer tied to storefront mode, so a CMS
     * snippet can move the image out without also losing the total and the
     * summary. When unset the stack renders inline.
     */
    mountSelector?: string;
}

/**
 * Stacked overlay of every selected Value's full-size `image`. Mobile-first:
 * fills the container width and takes its aspect ratio from the bottom
 * layer's own image. Returns `null` for components where the customer hasn't
 * opted into combined image rendering.
 *
 * 360-degree mode (`combined-product-image-360`) needs sequence + rotation
 * handling and is deferred — Phase 3 ships the static layer stack only.
 */
export function CombinedImage({ component, locale, mountSelector }: CombinedImageProps) {
    // A rule that removes a Characteristic removes its image layer too —
    // otherwise the product picture keeps showing a part the page just
    // explained isn't available.
    const selections = useEffectiveSelections();
    const mountEl = useExternalMount(mountSelector);
    // Measured from the bottom layer once the browser has it — see below.
    // Keyed by the src it was measured from so switching the base image
    // re-measures instead of keeping a stale shape.
    const [baseShape, setBaseShape] = useState<{ src: string; ratio: number } | null>(null);

    if (component.combinedImage !== 'combined-product-image') return null;

    const layers = collectCombinedImageLayers(component, selections);
    // The stack is `position: absolute` layers, so the container needs its own
    // height. A fixed square — which is what the stylesheet falls back to —
    // letterboxes every product that isn't square: doors, windows, awnings.
    // Legacy sized the container from the real image instead
    // (`height = (imageOriginalHeight / imageOriginalWidth) * width`,
    // combinedimage/combinedimage.js:236-237); this is the same thing
    // expressed as an aspect-ratio, taken from the bottom layer because that
    // is the base product every other layer is drawn onto.
    const baseSrc = layers[0]?.value.image;
    const baseRatio = baseShape && baseShape.src === baseSrc ? baseShape.ratio : undefined;

    // Keep the container in the DOM even when no layers — that way the
    // admin sees a reserved placeholder block instead of "nothing renders,
    // is it broken?" The container collapses to a thin neutral box that
    // disappears the moment the first selection lands a layer.
    const stack = (
        <div
            className="cfg-combined-image"
            data-configento-layers={layers.length}
            style={baseRatio !== undefined ? {
                aspectRatio: String(baseRatio),
                // Height-bound the stack: with the ratio known, a max-width is
                // the only way to shrink a too-tall picture as a whole — a bare
                // max-height would crop it, since the width is already definite.
                // Falls back to the stylesheet's max-width on a narrow column.
                maxWidth: `min(480px, calc(var(--cfg-combined-image-max-height) * ${baseRatio}))`,
            } : undefined}
            role="img"
            aria-label={layers.map((l) => pickLocale(l.value.title, locale) ?? '').join(', ')}
        >
            {layers.map((layer, idx) => (
                <img
                    key={`${layer.characteristicId}-${layer.value.id}`}
                    className="cfg-combined-image__layer"
                    src={layer.value.image}
                    alt=""
                    style={{ zIndex: idx }}
                    // Every layer of the stack is visible at once, so none of
                    // them may wait for a scroll: a lazy upper layer was the
                    // page's largest contentful paint on a Shopify product
                    // page (Lighthouse, 2026-09-23) and delayed it by a
                    // second. The bottom layer also decides the container's
                    // shape and gets the priority hint. Compared against
                    // `baseSrc` from this render, not `img.src`: the DOM
                    // resolves that to an absolute URL, which would never
                    // match the relative path the layer carries.
                    loading="eager"
                    fetchPriority={idx === 0 ? 'high' : undefined}
                    onLoad={idx === 0
                        ? (e) => {
                            const { naturalWidth: w, naturalHeight: h } = e.currentTarget;
                            if (baseSrc === undefined || w <= 0 || h <= 0) return;
                            setBaseShape({ src: baseSrc, ratio: w / h });
                        }
                        : undefined}
                />
            ))}
        </div>
    );

    // `undefined` → no selector requested (admin-iframe path) → inline.
    // `null` → selector set but no match on the host page (theme typo or
    // markup not present) → silent skip; inline would land the stack in
    // the configurator body where it doesn't belong.
    // HTMLElement → portal it into the host's gallery slot.
    if (mountEl === undefined) return stack;
    if (mountEl === null) return null;
    return createPortal(stack, mountEl);
}
